import { OrganizationListItemEditModal } from "@/components/organization-list/OrganizationListItemEditModal.tsx";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import i18n from "@/i18n.ts";
import {
  useOrganizationListByType,
  useOrganizationListSave,
} from "@/queries/use-organization-lists.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import {
  ApiOrganizationList,
  ApiOrganizationListItemPayload,
  ApiOrganizationListType,
  getOrganizationListTypeTitle,
} from "@/types/organization-list.types.ts";
import { yupResolver } from "@hookform/resolvers/yup";
import { DragDropContext, Draggable, Droppable, DropResult } from "@hello-pangea/dnd";
import {
  Box,
  Button,
  ButtonIcon,
  FontAwesomeIcon,
  Input,
  Skeleton,
  Stack,
  Text,
  Title,
  useModals,
} from "@skailer-com/uikit";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

const buildOrganizationListSchema = () =>
  yup.object({
    title: yup.string().trim().required(i18n.t("errors:field_required")),
    items: yup
      .array()
      .of(
        yup.object({
          id: yup.number().nullable().optional(),
          title: yup.string().trim().required(i18n.t("errors:field_required")),
        })
      )
      .required(),
  });

type OrganizationListSchema = yup.InferType<ReturnType<typeof buildOrganizationListSchema>>;

export interface OrganizationListManagerProps {
  type: ApiOrganizationListType;
  showTitleField?: boolean;
  submitLabel?: string;
  stickySubmit?: boolean;
  onSaved?: (organizationList: ApiOrganizationList) => void | Promise<void>;
}

function trackOrganizationListEdited(type: ApiOrganizationListType, listId: number) {
  switch (type) {
    case ApiOrganizationListType.termination_leave_types:
      analyticsService.trackEvent(analyticEvents.employeeTerminationLeaveTypes.edited, {
        [analyticProperties.id]: listId,
      });
      return;
    case ApiOrganizationListType.termination_reasons:
      analyticsService.trackEvent(analyticEvents.employeeDismissalReasons.edited, {
        [analyticProperties.id]: listId,
      });
      return;
    case ApiOrganizationListType.employee_position_change_reasons:
      analyticsService.trackEvent(analyticEvents.employeePositionChangeReasons.edited, {
        [analyticProperties.id]: listId,
      });
      return;
    default:
      return;
  }
}

export function OrganizationListManager({
  type,
  showTitleField = true,
  submitLabel,
  stickySubmit = false,
  onSaved,
}: OrganizationListManagerProps) {
  const { t } = useTranslation();
  const { openModal } = useModals();
  const { data: organizationList, isPending } = useOrganizationListByType(type);

  const organizationListSchema = buildOrganizationListSchema();

  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<OrganizationListSchema>({
    mode: "onSubmit",
    resolver: yupResolver(organizationListSchema),
    defaultValues: {
      title: "",
      items: [],
    },
  });

  const items = watch("items") ?? [];

  const organizationListSave = useOrganizationListSave(control);
  const itemsError =
    errors.items && "message" in errors.items && typeof errors.items.message === "string"
      ? errors.items.message
      : undefined;

  useEffect(() => {
    if (!organizationList) {
      return;
    }

    reset({
      title: organizationList.title,
      items: organizationList.items.map((item) => ({
        id: item.id,
        title: item.title,
      })),
    });
  }, [organizationList, reset]);

  const onSubmit = handleSubmit(async (values) => {
    if (!organizationList) {
      return;
    }

    const payloadItems: ApiOrganizationListItemPayload[] = values.items.map((item) => ({
      id: item.id ?? undefined,
      title: item.title.trim(),
    }));

    try {
      const savedOrganizationList = await organizationListSave.mutateAsync({
        listId: organizationList.id,
        payload: {
          title: values.title.trim(),
          items: payloadItems,
        },
      });

      trackOrganizationListEdited(type, savedOrganizationList.id);
      await onSaved?.(savedOrganizationList);
    } catch {
      return;
    }
  });

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) {
      return;
    }

    if (result.destination.index === result.source.index) {
      return;
    }

    const updatedItems = [...items];
    const [reorderedItem] = updatedItems.splice(result.source.index, 1);
    updatedItems.splice(result.destination.index, 0, reorderedItem);

    setValue("items", updatedItems, { shouldDirty: true });
  };

  const addItem = () => {
    openModal(OrganizationListItemEditModal, {
      itemTitle: "",
      onSave: (title: string) => {
        setValue("items", [...items, { title }], { shouldDirty: true });
      },
    });
  };

  const editItem = (index: number) => {
    const item = items[index];
    openModal(OrganizationListItemEditModal, {
      itemTitle: item.title,
      onSave: (title: string) => {
        const updatedItems = [...items];
        updatedItems[index] = { ...updatedItems[index], title };
        setValue("items", updatedItems, { shouldDirty: true });
      },
    });
  };

  const removeItem = (index: number) => {
    const updatedItems = [...items];
    updatedItems.splice(index, 1);
    setValue("items", updatedItems, { shouldDirty: true });
  };

  if (isPending) {
    return (
      <Stack gap="lg">
        {showTitleField && (
          <>
            <Skeleton className="h-5 w-32" />
            <Skeleton className="h-11 w-full" />
          </>
        )}
        <Stack gap="sm">
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} className="h-14 w-full" />
          ))}
        </Stack>
      </Stack>
    );
  }

  return (
    <form onSubmit={onSubmit} className={stickySubmit ? "flex flex-1 flex-col" : undefined}>
      <Stack gap="lg" className={stickySubmit ? "flex-1" : undefined}>
        {showTitleField && (
          <Input
            label={t("common:title")}
            required
            autoFocus
            type="text"
            placeholder={getOrganizationListTypeTitle(type, t)}
            {...register("title")}
            error={errors.title?.message}
          />
        )}

        <Stack gap="sm">
          <Stack direction="horizontal" justify="between" items="center">
            <Title header={t("common:items")} size="xs" paddingTop={false} paddingBottom={false} />
            <Button
              type="button"
              variant="secondary"
              leftIcon={<FontAwesomeIcon icon="fa-light fa-plus" />}
              onClick={addItem}
            >
              {t("common:add_item")}
            </Button>
          </Stack>
          <Text className="text-secondary">{t("common:drag_to_reorder")}</Text>
          {itemsError && <Text className="text-danger">{itemsError}</Text>}

          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId={`organization-list-items-${type}`}>
              {(provided) => (
                <div className="flex flex-col" {...provided.droppableProps} ref={provided.innerRef}>
                  {items.map((item, index) => (
                    <Draggable
                      key={`item-${item.id ?? index}`}
                      draggableId={`item-${item.id ?? index}`}
                      index={index}
                    >
                      {(provided) => (
                        <div
                          className="my-1 rounded-xs border border-dark/5 bg-dark/5 px-4 py-2"
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                        >
                          <Box>
                            <Stack direction="horizontal" justify="between" items="center">
                              <Text className="text-title">
                                {item.title || t("common:untitled")}
                              </Text>
                              <Stack direction="horizontal" gap="sm" items="center">
                                <ButtonIcon
                                  variant="plain"
                                  icon={<FontAwesomeIcon icon="fa-light fa-trash" />}
                                  onClick={() => removeItem(index)}
                                />
                                <ButtonIcon
                                  variant="plain"
                                  icon={<FontAwesomeIcon icon="fa-light fa-pen-to-square" />}
                                  onClick={() => editItem(index)}
                                />
                                <Box {...provided.dragHandleProps}>
                                  <FontAwesomeIcon
                                    className="m-2"
                                    icon="fa-light fa-grip-dots-vertical"
                                  />
                                </Box>
                              </Stack>
                            </Stack>
                          </Box>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </Stack>

        <Stack
          gap="sm"
          className={
            stickySubmit
              ? "sticky bottom-0 z-10 -mx-6 mt-auto border-t border-dark/5 bg-light px-6 py-4"
              : undefined
          }
        >
          <ServerErrorField errors={errors} />
          <Button
            type="submit"
            size="lg"
            isLoading={organizationListSave.isPending}
            disabled={!organizationList}
          >
            {submitLabel || t("common:save")}
          </Button>
        </Stack>
      </Stack>
    </form>
  );
}
